import { Injectable } from "@angular/core";
import { HttpClient, HttpResponse } from "@angular/common/http";
import { forkJoin, Observable, throwError } from "rxjs";
import { catchError, map, shareReplay, tap } from "rxjs/operators";
import type {
  PrintCardDownloadFile,
  PrintCardLayoutCatalogPreviewResult,
  PrintCardLayoutCatalogResponse,
  PrintCardOrderItemPreviewResult,
  PrintCardPageSizePresetListResponse,
  PrintCardPreviewDraftPayload,
  PrintCardPreviewResult,
  PrintCardPrinterSettingsResponse,
  PrintCardSettings,
  PrintCardTemplateItem,
  PrintCardTemplateListResponse,
  PrintCardTemplatePayload,
  PrintCardTemplateType,
} from "./print-cards.interface";
import { PRINT_CARD_TEMPLATE_TYPES } from "./print-cards.interface";

const PREVIEW_CACHE_LIMIT = 24;

function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) {
    return null;
  }
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim());
    } catch {
      return utf8Match[1].trim();
    }
  }
  const asciiMatch = header.match(/filename="([^"]+)"/i);
  if (asciiMatch?.[1]) {
    return asciiMatch[1].trim();
  }
  const plainMatch = header.match(/filename=([^;]+)/i);
  if (plainMatch?.[1]) {
    return plainMatch[1].trim().replace(/^"|"$/g, "");
  }
  return null;
}

@Injectable({
  providedIn: "root",
})
export class PrintCardService {
  private readonly pageSizePresetsCache = new Map<
    string,
    Observable<PrintCardPageSizePresetListResponse>
  >();
  private readonly printerSettingsCache = new Map<
    string,
    Observable<PrintCardPrinterSettingsResponse>
  >();
  private readonly layoutCatalogCache = new Map<
    string,
    Observable<PrintCardLayoutCatalogResponse>
  >();
  private readonly draftPreviewCache = new Map<
    string,
    Observable<PrintCardPreviewResult>
  >();
  private readonly layoutThumbsCache = new Map<
    string,
    Observable<PrintCardLayoutCatalogPreviewResult>
  >();
  private readonly templatePreviewCache = new Map<
    string,
    Observable<PrintCardPreviewResult>
  >();
  private editorCatalogWarm$: Observable<unknown> | null = null;

  constructor(private http: HttpClient) {}

  getPageSizePresets(
    type?: PrintCardTemplateType,
  ): Observable<PrintCardPageSizePresetListResponse> {
    const key = type ?? "ALL";
    let cached = this.pageSizePresetsCache.get(key);
    if (!cached) {
      const params = type ? { type } : undefined;
      cached = this.http
        .get<PrintCardPageSizePresetListResponse>("/print-cards/page-sizes", {
          params,
        })
        .pipe(shareReplay({ bufferSize: 1, refCount: false }));
      this.pageSizePresetsCache.set(key, cached);
    }
    return cached;
  }

  getPrinterSettings(
    type: PrintCardTemplateType,
  ): Observable<PrintCardPrinterSettingsResponse> {
    let cached = this.printerSettingsCache.get(type);
    if (!cached) {
      cached = this.http
        .get<PrintCardPrinterSettingsResponse>(
          "/print-cards/printer-settings",
          { params: { type } },
        )
        .pipe(shareReplay({ bufferSize: 1, refCount: false }));
      this.printerSettingsCache.set(type, cached);
    }
    return cached;
  }

  getLayoutCatalog(
    type?: PrintCardTemplateType,
  ): Observable<PrintCardLayoutCatalogResponse> {
    const key = type ?? "ALL";
    let cached = this.layoutCatalogCache.get(key);
    if (!cached) {
      const params = type ? { type } : undefined;
      cached = this.http
        .get<PrintCardLayoutCatalogResponse>("/print-cards/layout-catalog", {
          params,
        })
        .pipe(shareReplay({ bufferSize: 1, refCount: false }));
      this.layoutCatalogCache.set(key, cached);
    }
    return cached;
  }

  prefetchCatalogs(
    types: PrintCardTemplateType[] = PRINT_CARD_TEMPLATE_TYPES,
  ): Observable<unknown> {
    if (!this.editorCatalogWarm$) {
      this.editorCatalogWarm$ = forkJoin(
        types.flatMap((type) => [
          this.getPageSizePresets(type),
          this.getLayoutCatalog(type),
          this.getPrinterSettings(type),
        ]),
      ).pipe(shareReplay({ bufferSize: 1, refCount: false }));
    }
    return this.editorCatalogWarm$;
  }

  previewLayoutCatalog(payload: {
    email: string;
    type: PrintCardTemplateType;
    digitalCardProfileId?: string | null;
    profileLanguage?: string | null;
    settings?: Partial<PrintCardSettings>;
    compiledVariants?: Record<string, string>;
  }): Observable<PrintCardLayoutCatalogPreviewResult> {
    const key = JSON.stringify({
      email: payload.email.trim().toLowerCase(),
      type: payload.type,
      digitalCardProfileId: payload.digitalCardProfileId?.trim() || null,
      profileLanguage: payload.profileLanguage ?? null,
      settings: payload.settings ?? null,
      compiledVariants: payload.compiledVariants ?? null,
    });
    const cached = this.layoutThumbsCache.get(key);
    if (cached) {
      return cached;
    }
    const request$ = this.http
      .post<PrintCardLayoutCatalogPreviewResult>(
        "/print-cards/layout-catalog/preview",
        payload,
      )
      .pipe(
        catchError((error: unknown) => {
          this.layoutThumbsCache.delete(key);
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    this.layoutThumbsCache.set(key, request$);
    if (this.layoutThumbsCache.size > PREVIEW_CACHE_LIMIT) {
      const oldest = [...this.layoutThumbsCache.keys()][0];
      if (oldest) {
        this.layoutThumbsCache.delete(oldest);
      }
    }
    return request$;
  }

  previewDraft(
    payload: PrintCardPreviewDraftPayload,
  ): Observable<PrintCardPreviewResult> {
    const key = JSON.stringify({
      email: payload.email.trim().toLowerCase(),
      type: payload.type,
      frontContent: payload.frontContent,
      backContent: payload.backContent,
      digitalCardProfileId: payload.digitalCardProfileId?.trim() || null,
      profileLanguage: payload.profileLanguage ?? null,
      settings: payload.settings ?? null,
    });
    const cached = this.draftPreviewCache.get(key);
    if (cached) {
      return cached;
    }
    const request$ = this.http
      .post<PrintCardPreviewResult>("/print-cards/templates/preview", payload)
      .pipe(
        catchError((error: unknown) => {
          this.draftPreviewCache.delete(key);
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    this.draftPreviewCache.set(key, request$);
    if (this.draftPreviewCache.size > PREVIEW_CACHE_LIMIT) {
      const oldest = [...this.draftPreviewCache.keys()][0];
      if (oldest) {
        this.draftPreviewCache.delete(oldest);
      }
    }
    return request$;
  }

  getTemplates(): Observable<PrintCardTemplateListResponse> {
    return this.http.get<PrintCardTemplateListResponse>(
      "/print-cards/templates",
    );
  }

  getTemplate(printCardTemplateId: string): Observable<PrintCardTemplateItem> {
    return this.http.get<PrintCardTemplateItem>(
      `/print-cards/templates/${encodeURIComponent(printCardTemplateId)}`,
    );
  }

  createTemplate(
    payload: PrintCardTemplatePayload,
  ): Observable<PrintCardTemplateItem> {
    return this.http
      .post<PrintCardTemplateItem>("/print-cards/templates", payload)
      .pipe(tap(() => this.templatePreviewCache.clear()));
  }

  duplicateTemplate(
    printCardTemplateId: string,
  ): Observable<PrintCardTemplateItem> {
    return this.http
      .post<PrintCardTemplateItem>(
        `/print-cards/templates/${encodeURIComponent(printCardTemplateId)}/duplicate`,
        {},
      )
      .pipe(tap(() => this.templatePreviewCache.clear()));
  }

  updateTemplate(
    printCardTemplateId: string,
    payload: Partial<PrintCardTemplatePayload>,
  ): Observable<PrintCardTemplateItem> {
    return this.http
      .put<PrintCardTemplateItem>(
        `/print-cards/templates/${encodeURIComponent(printCardTemplateId)}`,
        payload,
      )
      .pipe(tap(() => this.templatePreviewCache.clear()));
  }

  setTemplateActive(
    printCardTemplateId: string,
    isActive: boolean,
  ): Observable<PrintCardTemplateItem> {
    return this.http
      .patch<PrintCardTemplateItem>(
        `/print-cards/templates/${encodeURIComponent(printCardTemplateId)}/active`,
        { isActive },
      )
      .pipe(tap(() => this.templatePreviewCache.clear()));
  }

  setTemplatePreview(
    printCardTemplateId: string,
    isPreview: boolean,
  ): Observable<PrintCardTemplateItem> {
    return this.http
      .patch<PrintCardTemplateItem>(
        `/print-cards/templates/${encodeURIComponent(printCardTemplateId)}/preview`,
        { isPreview },
      )
      .pipe(tap(() => this.templatePreviewCache.clear()));
  }

  approveTemplate(
    printCardTemplateId: string,
  ): Observable<PrintCardTemplateItem> {
    return this.http
      .post<PrintCardTemplateItem>(
        `/print-cards/templates/${encodeURIComponent(printCardTemplateId)}/approve`,
        {},
      )
      .pipe(tap(() => this.templatePreviewCache.clear()));
  }

  rejectTemplate(
    printCardTemplateId: string,
  ): Observable<PrintCardTemplateItem> {
    return this.http
      .post<PrintCardTemplateItem>(
        `/print-cards/templates/${encodeURIComponent(printCardTemplateId)}/reject`,
        {},
      )
      .pipe(tap(() => this.templatePreviewCache.clear()));
  }

  deleteTemplate(printCardTemplateId: string): Observable<void> {
    return this.http
      .delete<void>(
        `/print-cards/templates/${encodeURIComponent(printCardTemplateId)}`,
      )
      .pipe(tap(() => this.templatePreviewCache.clear()));
  }

  preview(
    printCardTemplateId: string,
    email: string,
    pageSizePresetId?: string,
    profileLanguage?: string | null,
  ): Observable<PrintCardPreviewResult> {
    const templateId = printCardTemplateId.trim();
    if (!templateId || templateId === "undefined") {
      return throwError(() => new Error("Print card template id is required"));
    }
    const key = JSON.stringify({
      templateId,
      email: email.trim().toLowerCase(),
      pageSizePresetId: pageSizePresetId?.trim() || null,
      profileLanguage: profileLanguage ?? null,
    });
    const cached = this.templatePreviewCache.get(key);
    if (cached) {
      return cached;
    }
    const request$ = this.http
      .post<PrintCardPreviewResult>(
        `/print-cards/templates/${encodeURIComponent(templateId)}/preview`,
        {
          email,
          ...(pageSizePresetId ? { pageSizePresetId } : {}),
          ...(profileLanguage ? { profileLanguage } : {}),
        },
      )
      .pipe(
        catchError((error: unknown) => {
          this.templatePreviewCache.delete(key);
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    this.templatePreviewCache.set(key, request$);
    if (this.templatePreviewCache.size > PREVIEW_CACHE_LIMIT) {
      const oldest = [...this.templatePreviewCache.keys()][0];
      if (oldest) {
        this.templatePreviewCache.delete(oldest);
      }
    }
    return request$;
  }

  getOrderItemPdf(printCardOrderId: string, itemId: string): Observable<Blob> {
    return this.http.get(
      `/print-cards/orders/${encodeURIComponent(printCardOrderId)}/items/${encodeURIComponent(itemId.trim())}/pdf`,
      { responseType: "blob" },
    );
  }

  getOrderItemPreview(
    printCardOrderId: string,
    itemId: string,
  ): Observable<PrintCardOrderItemPreviewResult> {
    return this.http.get<PrintCardOrderItemPreviewResult>(
      `/print-cards/orders/${encodeURIComponent(printCardOrderId)}/items/${encodeURIComponent(itemId.trim())}/preview`,
    );
  }

  downloadPdf(
    printCardTemplateId: string,
    email: string,
    pageSizePresetId: string,
  ): Observable<PrintCardDownloadFile> {
    const params = new URLSearchParams({
      email,
      pageSizePresetId,
    });
    return this.http
      .get(
        `/print-cards/templates/${encodeURIComponent(printCardTemplateId)}/pdf?${params.toString()}`,
        {
          responseType: "blob",
          observe: "response",
        },
      )
      .pipe(
        map((response) => ({
          blob: response.body ?? new Blob(),
          filename:
            parseContentDispositionFilename(
              response.headers.get("Content-Disposition"),
            ) ?? `print-card-${printCardTemplateId}.pdf`,
        })),
      );
  }

  downloadPrinterPdf(
    printCardTemplateId: string,
    email: string,
    pageSizePresetId: string,
  ): Observable<PrintCardDownloadFile> {
    const params = new URLSearchParams({
      email,
      pageSizePresetId,
    });
    return this.http
      .get(
        `/print-cards/templates/${encodeURIComponent(printCardTemplateId)}/pdf/printer?${params.toString()}`,
        {
          responseType: "blob",
          observe: "response",
        },
      )
      .pipe(
        map((response) => ({
          blob: response.body ?? new Blob(),
          filename:
            parseContentDispositionFilename(
              response.headers.get("Content-Disposition"),
            ) ?? `print-card-printer-${printCardTemplateId}.pdf`,
        })),
      );
  }

  downloadBulkEmployeesPdf(
    printCardTemplateId: string,
    pageSizePresetId: string,
    emails: string[],
  ): Observable<PrintCardDownloadFile> {
    return this.http
      .post(
        `/print-cards/templates/${encodeURIComponent(printCardTemplateId)}/pdf/bulk`,
        { pageSizePresetId, emails },
        { responseType: "blob", observe: "response" },
      )
      .pipe(
        map((response: HttpResponse<Blob>) => ({
          blob: response.body ?? new Blob(),
          filename:
            parseContentDispositionFilename(
              response.headers.get("Content-Disposition"),
            ) ?? "print-cards-bulk.pdf",
        })),
      );
  }
}
